import { slugify } from '@/utils/string';
import clsx from 'clsx';
import { AlertCircle, ChevronDown } from 'lucide-react';
import React from 'react';

export interface AppSelectOption {
  value: string;
  label: string;
}

export interface AppSelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  label?: React.ReactNode;
  options: AppSelectOption[];
  error?: string;
  hint?: string;
  selectClassName?: string;
}

const AppSelect = React.forwardRef<HTMLSelectElement, AppSelectProps>(
  (
    { label, options, error, hint, selectClassName = '', id, ...selectProps },
    ref
  ) => {
    const selectId =
      id ??
      (typeof label === 'string' ? `select-${slugify(label)}` : undefined);
    const isDisabled = selectProps.disabled;

    const borderClass =
      error && !isDisabled
        ? 'border-danger'
        : !isDisabled
          ? 'border-grey'
          : 'border-grey3';

    const bgClass = isDisabled ? 'bg-grey4' : error ? 'bg-dangerLight' : '';

    return (
      <div>
        {label && (
          <label
            htmlFor={selectId}
            className="mb-1.5 block text-sm font-medium text-textDark"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            className={clsx(
              'w-full h-[50px] rounded-[10px] border-[1.5px] bg-lighter pl-3.5 pr-9 text-[15px] outline-none transition-colors appearance-none',
              'text-textDark focus:bg-white focus:border-primary',
              borderClass,
              bgClass,
              isDisabled && 'text-grey10 cursor-not-allowed',
              selectClassName
            )}
            disabled={isDisabled}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${selectId}-error` : undefined}
            {...selectProps}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 w-[17px] h-[17px] flex items-center justify-center text-grey10 [&>svg]:w-[17px] [&>svg]:h-[17px]">
            <ChevronDown />
          </div>
        </div>
        {error ? (
          <p
            id={selectId ? `${selectId}-error` : undefined}
            className="mt-1 flex items-center gap-1 text-[13px] text-danger"
          >
            <AlertCircle className="size-4 shrink-0" aria-hidden />
            {error}
          </p>
        ) : hint ? (
          <p className="mt-1 text-[11.5px] leading-snug text-grey10">{hint}</p>
        ) : null}
      </div>
    );
  }
);

AppSelect.displayName = 'AppSelect';

export default AppSelect;
